import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    CheckBox,
    DataTable,
    Heading,
    Layer,
    Text,
} from 'grommet';
import { Close } from 'grommet-icons';
import PropTypes from 'prop-types';
import JobService from '../../api/job-service';
import CandidacyService from '../../api/candidacy-service';

export const ReverseMatchButton = ({ contactId }) => {
    const [showModal, setShowModal] = useState(false);

    return (
        <Box>
            <Button
                primary
                size="large"
                label="Find best jobs"
                onClick={() => setShowModal(true)}
            />
            {showModal && (
                <ReverseMatchesModal
                    contactId={contactId}
                    setShowModal={setShowModal}
                />
            )}
        </Box>
    );
};

const ReverseMatchesModal = ({ contactId, setShowModal }) => {
    const [buttonBusy, setButtonBusy] = useState(false);
    const [matchSuggestions, setMatchSuggestions] = useState();
    const [contractClientOnly, setContractClientOnly] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [fetchCount, setFetchCount] = useState(0);

    useEffect(() => {
        if (fetchCount === 0) return;
        let isActive = true;
        (async () => {
            setButtonBusy(true);
            let matches = null;
            try {
                matches = await new JobService().generateReverseMatches(
                    contactId,
                    { contractClientOnly },
                );
            } catch (error) {
                console.log(error);
            }
            if (!isActive) return;
            setMatchSuggestions(matches);
            setFetchError(matches === null);
            setButtonBusy(false);
        })();
        return () => {
            isActive = false;
        };
    }, [contactId, contractClientOnly, fetchCount]);

    const showEmptyContractClientText =
        contractClientOnly && matchSuggestions?.length === 0;

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                background="white"
                width="xlarge"
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                alignSelf="center"
                gap="30px">
                <Box direction="row" gap="20px" justify="between">
                    <Heading level="3" margin={{ vertical: 'none' }}>
                        Find best job matches for student
                    </Heading>
                    <Box align="end">
                        <Button
                            plain
                            aria-label="close"
                            icon={<Close />}
                            onClick={() => setShowModal(false)}
                        />
                    </Box>
                </Box>
                <Box>
                    <Box
                        width={{ min: 'medium' }}
                        align="center"
                        round="6px"
                        background="white">
                        <Text>
                            This will suggest the best job matches for this
                            student based on job weight fields.
                        </Text>
                    </Box>
                </Box>

                <Box align="center">
                    <CheckBox
                        toggle
                        label="Contract Client"
                        checked={contractClientOnly}
                        onChange={(event) =>
                            setContractClientOnly(event.target.checked)
                        }
                    />
                </Box>

                {fetchError && (
                    <Box align="center">
                        <Text color="status-critical">
                            Could not load job matches. Please try again.
                        </Text>
                    </Box>
                )}

                {showEmptyContractClientText && (
                    <Box align="center">
                        <Text>No contract client jobs match</Text>
                    </Box>
                )}

                {matchSuggestions && !showEmptyContractClientText && (
                    <Box overflow="scroll">
                        <JobMatchSuggestions
                            matches={matchSuggestions}
                            contactId={contactId}
                        />
                    </Box>
                )}

                <Box align="center">
                    <Button
                        primary
                        busy={buttonBusy}
                        label="Suggest matches"
                        style={{ width: '220px', height: '50px' }}
                        onClick={() => setFetchCount((count) => count + 1)}
                    />
                </Box>
            </Box>
        </Layer>
    );
};

const CreateCandidacyButton = ({ contactId, match }) => {
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState(false);

    const handleCreate = async () => {
        try {
            setBusy(true);
            await new CandidacyService().createCandidacy({
                contact: contactId,
                job: match.id,
                jsonData: {
                    score: match.score,
                    scoreLog: match.scoreLog,
                },
            });
            setCreated(true);
        } catch (error) {
            console.log(error);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box>
            <Button
                busy={busy}
                label={created ? 'Created' : 'Create'}
                disabled={created}
                primary
                onClick={handleCreate}
            />
        </Box>
    );
};

const JobMatchSuggestions = ({ matches, contactId }) => {
    const dataColumns = [
        {
            header: 'Job Title',
            render: (match) => (
                <Anchor
                    key={match.id}
                    href={`/jobs/${match.id}`}
                    label={match.title}
                />
            ),
            property: 'title',
        },
        {
            header: 'Company',
            property: 'company',
        },
        {
            header: 'Match Score',
            property: 'score',
        },
        {
            header: 'Score Breakdown',
            property: 'scoreLog',
        },
        {
            header: 'Action',
            property: 'id',
            render: (match) => (
                <CreateCandidacyButton contactId={contactId} match={match} />
            ),
        },
    ];

    return (
        <Box>
            <DataTable primaryKey="id" data={matches} columns={dataColumns} />
        </Box>
    );
};

ReverseMatchButton.propTypes = {
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CreateCandidacyButton.propTypes = {
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    match: PropTypes.object,
};

ReverseMatchesModal.propTypes = {
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setShowModal: PropTypes.func,
};

JobMatchSuggestions.propTypes = {
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    matches: PropTypes.array,
};
